import { Button, Scrollbar } from '@repo/go-ui';
import type { HsvaColor } from '@uiw/react-color';
import {
  EditableInput,
  EditableInputRGBA,
  hexToHsva,
  hsvaToHex,
  hsvaToHslaString,
  Hue,
  Saturation,
  validHex,
} from '@uiw/react-color';
import { clsx } from 'clsx';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import { throttleFn } from 'src/shared/utils/throttle-fn';

import type { PaletteSettingItem, PaletteSettingsStore } from '../palette-settings.store';

import styles from './colors-block.module.scss';

type Props = {
  store: PaletteSettingsStore;
};

const DEFAULT_COLORS: string[] = [
  '#FF6565',
  '#EE7B30',
  '#9A1917',
  '#8BB1F9',
  '#00A5FF',
  '#3F73F1',
  '#3033DF',
  '#F2C94C',
  '#896720',
  '#764700',
  '#73F74A',
  '#33771F',
  '#009A75',
  '#2C6887',
  '#FEB0FF',
  '#FF69B4',
  '#8E1B8F',
  '#BD69FF',
  '#6300B2',
  '#E0E0E0',
  '#616161',
];

const colorInputStyles = {
  width: '100%',
  display: 'flex',
  flexFlow: 'row nowrap',
  backgroundColor: 'var(--go-input-bg)',
  boxShadow: 'none',
  borderRadius: 4,
  fontSize: 13,
  padding: '4px 8px',
  color: 'var(--go-input-placeholder-text)',
};

const defHsva = {
  h: 0,
  s: 100,
  v: 50,
  a: 1,
};

export const ColorsBlock = observer<Props>(function ColorsBlock({ store }) {
  const [hsva, setHsva] = useState<HsvaColor>(defHsva);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback(
    throttleFn((color: HsvaColor | string, currentObject: PaletteSettingItem | null, subSection: string | null) => {
      setHsva(typeof color === 'string' ? hexToHsva(color) : color);

      if (!currentObject || !subSection) {
        return;
      }

      store.setColor(subSection, currentObject, typeof color === 'string' ? color : hsvaToHex(color));
    }, 0),
    [store],
  );

  useEffect(() => {
    return reaction(
      () => store.currentObject,
      (currentObject) => {
        if (!currentObject) {
          setHsva(defHsva);
          return;
        }

        const color =
          store.settingsItems[store.subSection!]?.active.find((i) => i === currentObject)?.color ??
          store.settingsItems[store.subSection!]?.archived.find((i) => i === currentObject)?.color;

        if (!color) {
          setHsva(defHsva);
        } else {
          setHsva(hexToHsva(color));
        }
      },
    );
  }, [store, store.currentObject]);

  if (!store.currentObject) {
    return null;
  }

  return (
    <Scrollbar>
      <div className={styles.wrapper}>
        <div className={styles.contentWrapper}>
          <Saturation
            className={styles.saturation}
            hsva={hsva}
            radius={8}
            style={{
              width: '100%',
            }}
            pointer={({ prefixCls, left, top }) => {
              return (
                <div
                  style={{
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16,
                    borderRadius: '100%',
                    backgroundColor: 'white',
                    top: `calc(${top} - 8px)`,
                    left: `calc(${left} - 8px)`,
                    cursor: 'pointer',
                  }}
                  className={prefixCls}
                >
                  <div
                    style={{
                      width: '50%',
                      height: '50%',
                      borderRadius: '100%',
                      backgroundColor: hsvaToHslaString(hsva),
                    }}
                  />
                </div>
              );
            }}
            onChange={(newColor) => {
              handleChange(newColor, store.currentObject, store.subSection);
            }}
          />
          <Hue
            className={styles.hue}
            radius={8}
            height={24}
            hue={hsva.h}
            pointer={({ prefixCls, left }) => {
              return (
                <div
                  style={{
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '100%',
                    backgroundColor: 'white',
                    left: `calc(${left} - 12px)`,
                    cursor: 'pointer',
                  }}
                  className={clsx(prefixCls, styles.pointer)}
                >
                  <div
                    style={{
                      width: '50%',
                      height: '50%',
                      borderRadius: '100%',
                      backgroundColor: `hsla(${hsva.h}, 100%, 50%, 1)`,
                    }}
                  />
                </div>
              );
            }}
            onChange={(newHue) => {
              const newState = { ...hsva, ...newHue };

              handleChange(newState, store.currentObject, store.subSection);
            }}
          />
          <EditableInput
            inputStyle={{ ...colorInputStyles }}
            className={styles.input}
            value={hsvaToHex(hsva)}
            onChange={(_, value) => {
              const valueString = String(value);

              if (validHex(valueString)) {
                const _hsva = { ...hexToHsva(valueString), a: 1 };
                handleChange(_hsva, store.currentObject, store.subSection);
              }
            }}
          />
          <EditableInputRGBA
            rProps={{ label: false, inputStyle: colorInputStyles, className: styles.input }}
            gProps={{ label: false, inputStyle: colorInputStyles, className: styles.input }}
            bProps={{ label: false, inputStyle: colorInputStyles, className: styles.input }}
            aProps={false}
            hsva={hsva}
            onChange={(color) => {
              const newValue = { ...hsva, ...color.hsva };
              handleChange(newValue, store.currentObject, store.subSection);
            }}
          />

          <div className={styles.defaultColorsBlock}>
            {DEFAULT_COLORS.map((color) => {
              const isActive = store.currentObject?.color === color;

              return (
                <Button
                  key={color}
                  className={clsx(styles.defaultColorButton, isActive && styles.defaultColorButton__active)}
                  variant="flat"
                  style={{
                    backgroundColor: color,
                  }}
                  onClick={() => handleChange(color, store.currentObject, store.subSection)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </Scrollbar>
  );
});
